import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { AppShell } from '../components/AppShell'
import { ScheduleTable } from '../components/schedule/ScheduleTable'
import { LessonFormDialog } from '../components/schedule/LessonFormDialog'
import { useEntityList } from '../api/hooks/useEntity'
import { ENTITY, type Lesson, type SchoolClass, type Subject } from '../types/entities'

/**
 * Расписание: сетка день × № урока. Page-компонент держит state фильтра
 * и редактирования, расчёт индексов lessonByCell/subjectById, рендер
 * delegirует ScheduleTable; форму урока — LessonFormDialog.
 */
export function SchedulePage() {
  const { t } = useTranslation()
  const classes = useEntityList<SchoolClass>(ENTITY.schoolClass)
  const subjects = useEntityList<Subject>(ENTITY.subject)
  const lessons = useEntityList<Lesson>(ENTITY.lesson)

  const activeClasses = useMemo(
    () =>
      (classes.data ?? [])
        .filter((c) => c.archivedAt == null)
        .sort((a, b) => a.gradeLevel - b.gradeLevel || a.name.localeCompare(b.name)),
    [classes.data],
  )

  const [classId, setClassId] = useState<string>('')
  const [editing, setEditing] = useState<{ lesson?: Lesson; day: number; slot: number } | null>(null)

  useEffect(() => {
    if (!classId && activeClasses[0]) setClassId(activeClasses[0].id)
  }, [classId, activeClasses])

  const lessonByCell = useMemo(() => {
    const m = new Map<string, Lesson>()
    for (const l of lessons.data ?? []) {
      if (l.classId !== classId) continue
      m.set(`${l.dayOfWeek}-${l.slot}`, l)
    }
    return m
  }, [lessons.data, classId])

  const subjectById = useMemo(() => {
    const m = new Map<string, Subject>()
    for (const s of subjects.data ?? []) m.set(s.id, s)
    return m
  }, [subjects.data])

  if (activeClasses.length === 0) {
    return (
      <AppShell title={t('schedulePage.title')}>
        <NoClasses />
      </AppShell>
    )
  }

  return (
    <AppShell
      title={t('schedulePage.title')}
      subtitle={t('schedulePage.subtitle')}
      actions={
        <select
          className="input-field max-w-[200px]"
          value={classId}
          onChange={(e) => setClassId(e.target.value)}
        >
          {activeClasses.map((c) => (
            <option key={c.id} value={c.id}>{c.name} ({c.gradeLevel})</option>
          ))}
        </select>
      }
    >
      {(subjects.data ?? []).length === 0 ? (
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 text-sm text-yellow-900 mb-4">
          {t('schedulePage.needSubjectsPrefix')}{' '}
          <a href="/app/subjects" className="text-primary underline font-medium">{t('schedulePage.createSubjectsLink')}</a>.
        </div>
      ) : (
        <ScheduleTable
          lessonByCell={lessonByCell}
          subjectById={subjectById}
          onCellClick={(day, slot, lesson) => setEditing({ lesson, day, slot })}
        />
      )}

      {editing && (
        <LessonFormDialog
          classId={classId}
          day={editing.day}
          slot={editing.slot}
          lesson={editing.lesson}
          subjects={subjects.data ?? []}
          onClose={() => setEditing(null)}
        />
      )}
    </AppShell>
  )
}

function NoClasses() {
  const { t } = useTranslation()
  return (
    <div className="text-center py-12 bg-gradient-to-br from-primary-soft/30 to-white border border-purple-100/80 rounded-2xl">
      <div className="text-5xl mb-3">📅</div>
      <h3 className="font-semibold text-gray-900 mb-2">{t('schedulePage.noClassesTitle')}</h3>
      <a href="/app/classes" className="btn-primary text-sm inline-block">{t('schedulePage.toClasses')}</a>
    </div>
  )
}
